import { useRef, useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, X, ExternalLink, Music2 } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";
import { loadSpotifyIframeApi } from "../lib/spotifyIframe";
import { track } from "../lib/analytics";
import { api } from "../lib/api";
import FollowFavorite from "./FollowFavorite";
import { SpotifyIcon } from "./icons/BrandIcons";

export default function PersistentPlayer() {
  const { currentRelease, currentTrack, playToken, setIsPlaying } = usePlayer();
  const controllerRef = useRef(null);
  const elRef = useRef(null);
  const createdRef = useRef(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  // genuine playback tracking (per-track)
  const playedMs = useRef(0);
  const lastPos = useRef(null);
  const fired = useRef(new Set());

  const [ctaVisible, setCtaVisible] = useState(false);
  const [ctaDismissed, setCtaDismissed] = useState(false);
  const [fallback, setFallback] = useState(false);

  const release = currentRelease;

  const openSpotify = useCallback((fromCta) => {
    if (!release?.spotifyURL) return;
    track("spotify_cta_clicked", { releaseId: release.internalId, trackId: currentTrack?.trackId });
    window.open(release.spotifyURL, "_blank", "noopener");
  }, [release, currentTrack]);

  const resetMilestones = useCallback(() => {
    playedMs.current = 0;
    lastPos.current = null;
    fired.current = new Set();
    setCtaVisible(false);
    setCtaDismissed(false);
    setFallback(false);
  }, []);

  const onUpdate = useCallback((e) => {
    const rel = controllerRef.current?.__release;
    const data = e.data || {};
    const position = data.position || 0;
    const duration = data.duration || 0;
    const isPaused = data.isPaused;

    if (!isPaused) {
      setIsPlaying(true);
      setFallback(false);
      if (!fired.current.has("play")) {
        fired.current.add("play");
        track("spotify_play_started", { releaseId: rel?.id, trackId: rel?.trackId });
      }
      if (lastPos.current != null && position > lastPos.current && position - lastPos.current < 4000) {
        playedMs.current += position - lastPos.current;
      }
    } else {
      setIsPlaying(false);
    }
    lastPos.current = position;

    if (playedMs.current >= 10000 && !fired.current.has("s10")) {
      fired.current.add("s10");
      track("spotify_10_seconds", { releaseId: rel?.id, trackId: rel?.trackId });
    }
    // 30-second Spotify playback milestone ("spotify_30_seconds")
    if (playedMs.current >= 30000 && !fired.current.has("s30")) {
      fired.current.add("s30");
      track("spotify_30_seconds", { releaseId: rel?.id, trackId: rel?.trackId });
      if (!ctaDismissed) {
        setCtaVisible(true);
        track("spotify_cta_displayed", { releaseId: rel?.id, trackId: rel?.trackId });
      }
    }
    if (duration > 0) {
      if (position / duration >= 0.5 && !fired.current.has("p50")) {
        fired.current.add("p50");
        track("spotify_50_percent", { releaseId: rel?.id, trackId: rel?.trackId });
      }
      if (position / duration >= 0.9 && !fired.current.has("p90")) {
        fired.current.add("p90");
        track("spotify_90_percent", { releaseId: rel?.id, trackId: rel?.trackId });
      }
      if (duration - position < 1500 && position > 1000 && !fired.current.has("done")) {
        fired.current.add("done");
        track("spotify_track_completed", { releaseId: rel?.id, trackId: rel?.trackId });
      }
    }
  }, [ctaDismissed, setIsPlaying]);

  // Create controller once, then re-use it for subsequent tracks.
  useEffect(() => {
    if (!currentTrack) return;
    resetMilestones();

    const meta = { id: release?.internalId, trackId: currentTrack.trackId };

    const startFallbackTimer = () => {
      setTimeout(() => {
        if (!fired.current.has("play")) setFallback(true);
      }, 9000);
    };

    if (createdRef.current && controllerRef.current) {
      controllerRef.current.__release = meta;
      controllerRef.current.loadUri(currentTrack.uri);
      setTimeout(() => {
        try { controllerRef.current.play(); } catch {}
      }, 350);
      startFallbackTimer();
      return;
    }

    createdRef.current = true;
    loadSpotifyIframeApi().then((IFrameAPI) => {
      if (!elRef.current) return;
      IFrameAPI.createController(
        elRef.current,
        { uri: currentTrack.uri, width: "100%", height: "80" },
        (controller) => {
          controllerRef.current = controller;
          controller.__release = meta;
          controller.addListener("playback_update", onUpdate);
          controller.addListener("ready", () => {
            try { controller.play(); } catch {}
          });
          startFallbackTimer();
        }
      );
    });
  }, [playToken]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!release) return null;

  return (
    <>
      {/* 30-second engagement CTA (does not stop playback) */}
      <AnimatePresence>
        {ctaVisible && !ctaDismissed && (
          <motion.div
            data-testid="engagement-cta"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            className="fixed left-3 right-3 bottom-[104px] z-[60] mx-auto max-w-lg"
          >
            <div className="glass gold-glow rounded-2xl p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold">
                  <Flame size={22} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-display text-sm font-semibold leading-tight text-white">
                    Feeling this one?
                  </p>
                  <p className="truncate text-xs text-white/60">
                    Keep listening or stay connected
                  </p>
                </div>
                <button
                  data-testid="cta-open-spotify"
                  onClick={() => openSpotify(true)}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-gold px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-black transition-transform active:scale-95"
                >
                  <SpotifyIcon size={14} /> Open in Spotify <ExternalLink className="inline" size={13} />
                </button>
                <button
                  data-testid="cta-dismiss"
                  aria-label="Dismiss"
                  onClick={() => { setCtaDismissed(true); setCtaVisible(false); }}
                  className="shrink-0 rounded-full p-1.5 text-white/50 transition-colors hover:text-white"
                >
                  <X size={18} />
                </button>
              </div>
              <FollowFavorite settings={settings} compact />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Persistent player bar */}
      <motion.div
        data-testid="persistent-player"
        initial={{ y: 120 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 28 }}
        className="fixed inset-x-0 bottom-0 z-50"
      >
        <div className="glass border-t border-white/10">
          <div className="mx-auto flex max-w-3xl items-center gap-3 px-3 py-2.5">
            <img
              src={release.artwork}
              alt={`${release.title} artwork`}
              className="h-12 w-12 shrink-0 rounded-lg object-cover"
              onError={(e) => { e.currentTarget.style.opacity = 0.2; }}
            />
            <div className="min-w-0 flex-1">
              <p data-testid="player-title" className="truncate font-display text-sm font-semibold text-white">
                {currentTrack?.title || release.title}
              </p>
              <p className="truncate text-xs text-gold">DJ Código</p>
            </div>
            <a
              data-testid="player-open-spotify"
              href={release.spotifyURL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track("spotify_cta_clicked", { releaseId: release.internalId })}
              className="inline-flex shrink-0 items-center gap-1 rounded-full border border-gold/40 bg-gold/10 px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-gold transition-transform active:scale-95"
            >
              <SpotifyIcon size={13} /> Spotify <ExternalLink className="inline" size={12} />
            </a>
          </div>
          {/* Official Spotify embed — real playback source */}
          <div className="spotify-embed mx-auto max-w-3xl px-3 pb-2">
            <div ref={elRef} />
            {fallback && (
              <div
                data-testid="player-fallback"
                className="mt-1 flex items-center gap-2 rounded-lg bg-white/5 px-3 py-2 text-xs text-white/70"
              >
                <Music2 size={14} className="text-gold" />
                Keep listening on Spotify —
                <a href={release.spotifyURL} target="_blank" rel="noopener noreferrer" className="font-semibold text-gold underline">
                  Open in Spotify
                </a>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </>
  );
}
